import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useSkin } from "../contexts/SkinContext";
import "./InputArea.css"; // 导入新的CSS文件

const PROMPT_HISTORY_STORAGE_PREFIX = "hammerath_prompt_history_v1";
const MAX_RECENT_PROMPTS = 100;

const getPromptHistoryStorageKey = () => {
  try {
    const rawUser = localStorage.getItem("hammerath_current_user") || localStorage.getItem("hammerath_user");
    const user = rawUser ? JSON.parse(rawUser) : null;
    const userKey = user?.id || user?.user_id || user?.username || "local";
    return `${PROMPT_HISTORY_STORAGE_PREFIX}_${String(userKey)}`;
  } catch (_) {
    return `${PROMPT_HISTORY_STORAGE_PREFIX}_local`;
  }
};

const normalizePromptHistory = (entries) => {
  if (!Array.isArray(entries)) return [];

  let recentCount = 0;
  return entries
    .filter((item) => item && typeof item.text === "string" && item.text.trim())
    .map((item) => ({
      text: item.text.trim(),
      isFavorite: Boolean(item.isFavorite),
      updatedAt: Number(item.updatedAt) || 0,
    }))
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .filter((item) => {
      if (item.isFavorite) return true;
      recentCount += 1;
      return recentCount <= MAX_RECENT_PROMPTS;
    });
};

const loadPromptHistory = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(getPromptHistoryStorageKey()) || "[]");
    return normalizePromptHistory(saved);
  } catch (_) {
    return [];
  }
};

const persistPromptHistory = (entries) => {
  try {
    localStorage.setItem(getPromptHistoryStorageKey(), JSON.stringify(entries));
  } catch (_) {
    // 存储空间不足或隐私模式不可写时，不影响正常生成流程。
  }
};

// 检测输入内容是否包含敏感词或追问模型原理
export const checkSensitiveContent = (text) => {
  if (!text) return { hasSensitive: false };
  const normalizedText = String(text).toLowerCase();
  const compactText = normalizedText.replace(/\s+/g, "");
  const englishCompactText = normalizedText.replace(/[^a-z0-9]/g, "");

  // 1. 政治敏感元素（国家领导人、宪法法律、敏感政治词汇等）
  const politicalPatterns = [
    /习\s{0,3}近\s{0,3}平/i,
    /习\s{0,3}主\s{0,3}席/i,
    /习\s{0,3}大\s{0,3}大/i,
    /李\s{0,3}强/i,
    /毛\s{0,3}泽\s{0,3}东/i,
    /邓\s{0,3}小\s{0,3}平/i,
    /江\s{0,3}泽\s{0,3}民/i,
    /胡\s{0,3}锦\s{0,3}涛/i,
    /温\s{0,3}家\s{0,3}宝/i,
    /朱\s{0,3}镕\s{0,3}基/i,
    /周\s{0,3}恩\s{0,3}来/i,
    /孙\s{0,3}中\s{0,3}山/i,
    /蒋\s{0,3}介\s{0,3}石/i,
    /拜\s{0,3}登/i,
    /特\s{0,3}朗\s{0,3}普/i,
    /普\s{0,3}京/i,
    /金\s{0,3}正\s{0,3}恩/i,
    /反\s{0,3}分\s{0,3}裂\s{0,3}国\s{0,3}家\s{0,3}法/i,
    /反\s{0,3}分\s{0,3}裂/i,
    /中\s{0,3}国\s{0,3}共\s{0,3}产\s{0,3}党/i,
    /共\s{0,3}产\s{0,3}党/i,
    /国\s{0,3}民\s{0,3}党/i,
    /中\s{0,3}共/i,
    /宪\s{0,3}法/i,
    /法\s{0,3}律/i,
    /党\s{0,3}章/i,
    /天\s{0,3}安\s{0,3}门\s{0,3}事\s{0,3}件/i,
    /天\s{0,3}安\s{0,3}门/i,
    /六\s{0,3}四/i,
    /法\s{0,3}轮\s{0,3}功/i,
    /疆\s{0,3}独/i,
    /藏\s{0,3}独/i,
    /港\s{0,3}独/i,
    /台\s{0,3}独/i,
    /政\s{0,3}治\s{0,3}敏\s{0,3}感/i,
    /言\s{0,3}论\s{0,3}自\s{0,3}由/i,
    /人\s{0,3}权/i,
    /民\s{0,3}主/i
  ];

  // 2. 模型、原理、开发者等追问相关敏感词及句子
  const modelPatterns = [
    // 完整问句/长句
    /你\s{0,3}是\s{0,3}基\s{0,3}于\s{0,3}什\s{0,3}么\s{0,3}模\s{0,3}型\s{0,3}开\s{0,3}发\s{0,3}的/i,
    /你\s{0,3}是\s{0,3}什\s{0,3}么\s{0,3}模\s{0,3}型\s{0,3}开\s{0,3}发\s{0,3}的/i,
    /你\s{0,3}是\s{0,3}什\s{0,3}么\s{0,3}大\s{0,3}模\s{0,3}型/i,
    /你\s{0,3}是\s{0,3}什\s{0,3}么\s{0,3}模\s{0,3}型/i,
    /你\s{0,3}是\s{0,3}哪\s{0,3}个\s{0,3}模\s{0,3}型/i,
    /你\s{0,3}是\s{0,3}什\s{0,3}么\s{0,3}L\s{0,3}L\s{0,3}M/i,
    /你\s{0,3}是\s{0,3}G\s{0,3}P\s{0,3}T\s{0,3}吗/i,
    /你\s{0,3}是\s{0,3}C\s{0,3}l\s{0,3}a\s{0,3}u\s{0,3}d\s{0,3}e\s{0,3}吗/i,
    /you\s{0,3}are\s{0,3}GPT/i,
    /you\s{0,3}are\s{0,3}Claude/i,
    /你\s{0,3}是\s{0,3}G\s{0,3}e\s{0,3}m\s{0,3}i\s{0,3}n\s{0,3}i\s{0,3}吗/i,
    /你\s{0,3}的\s{0,3}工\s{0,3}作\s{0,3}原\s{0,3}理\s{0,3}是\s{0,3}什\s{0,3}么/i,
    /你\s{0,3}的\s{0,3}实\s{0,3}现\s{0,3}原\s{0,3}理\s{0,3}是\s{0,3}什\s{0,3}么/i,
    /你\s{0,3}的\s{0,3}原\s{0,3}理\s{0,3}是\s{0,3}什\s{0,3}么/i,
    /你\s{0,3}是\s{0,3}怎\s{0,3}么\s{0,3}实\s{0,3}现\s{0,3}的/i,
    /你\s{0,3}是\s{0,3}怎\s{0,3}么\s{0,3}工\s{0,3}作\s{0,3}的/i,
    /你\s{0,3}的\s{0,3}工\s{0,3}作\s{0,3}原\s{0,3}理/i,
    /你\s{0,3}的\s{0,3}实\s{0,3}现\s{0,3}原\s{0,3}理/i,
    /你\s{0,3}的\s{0,3}底\s{0,3}层\s{0,3}原\s{0,3}理/i,
    /你\s{0,3}的\s{0,3}底\s{0,3}层\s{0,3}逻\s{0,3}辑/i,
    /你\s{0,3}的\s{0,3}运\s{0,3}行\s{0,3}机\s{0,3}制/i,
    /你\s{0,3}背\s{0,3}后\s{0,3}的\s{0,3}技\s{0,3}术/i,
    
    // 核心词/短语
    /基\s{0,3}于\s{0,3}什\s{0,3}么\s{0,3}模\s{0,3}型/i,
    /什\s{0,3}么\s{0,3}大\s{0,3}模\s{0,3}型/i,
    /什\s{0,3}么\s{0,3}模\s{0,3}型/i,
    /哪\s{0,3}个\s{0,3}模\s{0,3}型/i,
    /什\s{0,3}么\s{0,3}L\s{0,3}L\s{0,3}M/i,
    /G\s{0,3}P\s{0,3}T\s{0,3}吗/i,
    /C\s{0,3}l\s{0,3}a\s{0,3}u\s{0,3}d\s{0,3}e\s{0,3}吗/i,
    /G\s{0,3}e\s{0,3}m\s{0,3}i\s{0,3}n\s{0,3}i\s{0,3}吗/i,
    /大\s{0,3}语\s{0,3}言\s{0,3}模\s{0,3}型/i,
    /使\s{0,3}用\s{0,3}的\s{0,3}模\s{0,3}型/i,
    /底\s{0,3}座\s{0,3}模\s{0,3}型/i,
    /工\s{0,3}作\s{0,3}原\s{0,3}理/i,
    /实\s{0,3}现\s{0,3}原\s{0,3}理/i,
    /技\s{0,3}术\s{0,3}架\s{0,3}构/i,
    /技\s{0,3}术\s{0,3}栈/i,
    /背\s{0,3}后\s{0,3}的\s{0,3}技\s{0,3}术/i,
    /怎\s{0,3}么\s{0,3}工\s{0,3}作\s{0,3}的/i,
    /运\s{0,3}行\s{0,3}机\s{0,3}制/i,
    /底\s{0,3}层\s{0,3}逻\s{0,3}辑/i,
    /底\s{0,3}层\s{0,3}原\s{0,3}理/i,
    /谁\s{0,3}开\s{0,3}发\s{0,3}的/i,
    /哪\s{0,3}个\s{0,3}公\s{0,3}司\s{0,3}开\s{0,3}发\s{0,3}的/i,
    /哪\s{0,3}家\s{0,3}公\s{0,3}司\s{0,3}开\s{0,3}发\s{0,3}的/i,
    /开\s{0,3}发\s{0,3}者\s{0,3}是\s{0,3}谁/i,
    /谁\s{0,3}制\s{0,3}作\s{0,3}的/i,
    /谁\s{0,3}创\s{0,3}造\s{0,3}的/i
  ];

  for (const pattern of politicalPatterns) {
    if (pattern.test(text)) {
      return { hasSensitive: true, type: "political" };
    }
  }

  for (const pattern of modelPatterns) {
    if (pattern.test(text)) {
      return { hasSensitive: true, type: "model" };
    }
  }

  // 3. 英文完整句式拦截：覆盖 "are you a model from google" 等表达
  const englishModelQueryPatterns = [
    /\bare\s+you\s+(a\s+)?(google|openai|anthropic|deepseek|xai|mistral|meta)\s+(model|llm)\b/i,
    /\bare\s+you\s+(from|by)\s+(google|openai|anthropic|deepseek|xai|mistral|meta)\b/i,
    /\b(what|which)\s+(model|llm)\s+are\s+you\b/i,
    /\bwhich\s+company\s+(built|developed|created|trained|made)\s+you\b/i,
    /\bwhat\s+company\s+(built|developed|created|trained|made)\s+you\b/i,
    /\bwho\s+(built|developed|created|trained|made)\s+you\b/i,
    /\bwhat\s+(is|s)\s+your\s+(base|foundation)\s+model\b/i,
    /\bwhat\s+model\s+are\s+you\s+based\s+on\b/i
  ];
  for (const pattern of englishModelQueryPatterns) {
    if (pattern.test(normalizedText)) {
      return { hasSensitive: true, type: "model" };
    }
  }

  // 4. 补充语义拦截：厂商词 + 模型追问语义组合（例如“你是google的模型吗”）
  const modelVendorKeywords = [
    "google", "谷歌", "openai", "anthropic", "deepseek", "xai", "mistral", "meta",
    "kimi", "moonshot", "qwen", "通义", "wenxin", "文心", "hunyuan", "混元", "spark", "星火"
  ];
  const hasVendorKeyword = modelVendorKeywords.some((keyword) => compactText.includes(keyword));
  const hasModelKeyword =
    compactText.includes("模型") ||
    compactText.includes("llm") ||
    compactText.includes("大语言") ||
    compactText.includes("model") ||
    compactText.includes("largelanguagemodel") ||
    compactText.includes("basemodel") ||
    compactText.includes("foundationmodel");
  const hasIdentityQuery =
    compactText.includes("你是") ||
    compactText.includes("你是不是") ||
    compactText.includes("areyou") ||
    compactText.includes("whatareyou") ||
    compactText.includes("whatmodelareyou") ||
    compactText.includes("whichmodelareyou");
  const hasDeveloperQuery =
    compactText.includes("谁开发") ||
    compactText.includes("哪家公司开发") ||
    compactText.includes("whichcompanybuilt") ||
    compactText.includes("whichcompanydeveloped") ||
    compactText.includes("whatcompanybuilt") ||
    compactText.includes("whobuilt") ||
    compactText.includes("whodeveloped") ||
    englishCompactText.includes("whichcompanybuiltyou") ||
    englishCompactText.includes("whichcompanydevelopedyou") ||
    englishCompactText.includes("whatcompanybuiltyou") ||
    englishCompactText.includes("whobuiltyou") ||
    englishCompactText.includes("whodevelopedyou");

  if (hasVendorKeyword && hasModelKeyword && (hasIdentityQuery || hasDeveloperQuery)) {
    return { hasSensitive: true, type: "model" };
  }

  return { hasSensitive: false };
};

const InputArea = ({
  onSubmit,
  isLoading,
  mode = "manim",
  placeholder,
  showEnhanceButton = true,
  availableModes = [],
  onModeChange,
  extraContent,
  value,
  onChange,
  is3DMode,
  setIs3DMode,
  isFreeMode = false,
  onFreeModeChange,
  isDeepThinking = false,
  onDeepThinkingChange,
  enhanceCreditCost = 1,
}) => {
  const [internalInput, setInternalInput] = useState("");
  const [promptHistory, setPromptHistory] = useState(loadPromptHistory);
  const isControlled = value !== undefined && onChange !== undefined;
  const input = isControlled ? value : internalInput;

  const setInput = (val) => {
    if (isControlled) {
      onChange(val);
    } else {
      setInternalInput(val);
    }
  };
  const MAX_IMAGES = 3;
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [showModeDropdown, setShowModeDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const t = useTranslation(); // 多语言翻译
  const { isMidAutumnMemphis } = useSkin();

  // 本地兜底说明
  const localExplanation = useMemo(() => {
    const explanations = {
      manim: t.modeExplanationSceneManim || "生成专业精致的数学、几何、物理等教学动画，适合微课、教案展示和自媒体内容创作。",
      manimgx: t.modeExplanationSceneManimgx || "基于 ManimGX 引擎的高性能动画生成，支持现代化扩展语法与极速渲染。",
      geogebra: t.modeExplanationSceneGeogebra || "绘制2D或3D函数图像与动态几何画板，适合在课堂或白板上进行交互式数学演示与作图。",
      threejs: t.modeExplanationSceneThreejs || "构建交互式3D空间场景，适合立体几何、空间立体图像的可视化展示及自由拖拽交互。",
      solve: t.modeExplanationSceneSolve || "针对数学与物理的公式、图形和多步骤题目进行智能深度解析，给出详细解题步骤与答案。",
      qq: t.modeExplanationSceneQq || "将复杂的概念或知识结构一键生成清晰的交互式思维导图/概念图，适合快速梳理知识脉络。",
    };
    return explanations[mode] || "";
  }, [mode, t]);

  const currentConfig = useMemo(() => {
    return availableModes.find((m) => m.key === mode);
  }, [availableModes, mode]);

  const displayCredits = useMemo(() => {
    const cost = Number(currentConfig?.credit_cost);
    return Number.isFinite(cost) && cost >= 0 ? cost : 1;
  }, [currentConfig]);

  // 获取深度思考（AI修饰）消耗的积分，优先取prompt_enhance配置的credit_cost或父组件传入的enhanceCreditCost
  const enhanceCost = useMemo(() => {
    const enhanceMode = availableModes.find((m) => m.key === "prompt_enhance");
    if (enhanceMode && enhanceMode.credit_cost !== undefined && enhanceMode.credit_cost !== null) {
      const modeCost = Number(enhanceMode.credit_cost);
      if (Number.isFinite(modeCost) && modeCost >= 0) {
        return modeCost;
      }
    }
    if (enhanceCreditCost !== undefined && enhanceCreditCost !== null) {
      const propCost = Number(enhanceCreditCost);
      if (Number.isFinite(propCost) && propCost >= 0) {
        return propCost;
      }
    }
    return 1;
  }, [availableModes, enhanceCreditCost]);

  // 深度思考模式总消耗 = 当前模式基础积分 + 深度思考积分
  const deepCredits = useMemo(() => {
    return displayCredits + enhanceCost;
  }, [displayCredits, enhanceCost]);

  const freeModeAvailable = Boolean(currentConfig?.free_available);

  const displayDescription = useMemo(() => {
    return currentConfig?.description || localExplanation;
  }, [currentConfig, localExplanation]);

  // 监听窗口大小变化，适配移动端布局
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 监听教程强制展开模式菜单的事件
  useEffect(() => {
    const handleOpenModes = () => setShowModeDropdown(true);
    const handleCloseModes = () => setShowModeDropdown(false);
    
    window.addEventListener('tutorialOpenModes', handleOpenModes);
    window.addEventListener('tutorialCloseModes', handleCloseModes);
    
    return () => {
      window.removeEventListener('tutorialOpenModes', handleOpenModes);
      window.removeEventListener('tutorialCloseModes', handleCloseModes);
    };
  }, []);

  // 切换积分/计算模式（免费 / 标准积分 / 深度思考）
  const handleUsageTierSelect = (tier) => {
    if (tier === "free") {
      onFreeModeChange?.(true);
      onDeepThinkingChange?.(false);
    } else if (tier === "standard") {
      onFreeModeChange?.(false);
      onDeepThinkingChange?.(false);
    } else if (tier === "deep") {
      onFreeModeChange?.(false);
      onDeepThinkingChange?.(true);
    }
  };

  // 根据模式获取标题和按钮文本 - 使用useMemo确保响应语言变化
  const uiTexts = useMemo(() => {
    if (mode === "manimgx") {
      return {
        title: t.inputTitleManimgx || "描述你想要的 ManimGX 动画",
        icon: "ri-flashlight-line",
        defaultPlaceholder: placeholder || t.manimgxPlaceholder || t.inputPlaceholder,
      };
    } else if (mode === "threejs") {
      return {
        title: t.inputTitleThreejs,
        icon: "ri-computer-line",
        defaultPlaceholder: placeholder || t.threejsPlaceholder,
      };
    } else if (mode === "solve") {
      return {
        title: t.inputTitleSolve,
        icon: "ri-hammer-line",
        defaultPlaceholder: placeholder || t.solvePlaceholder,
      };
    } else if (mode === "geogebra") {
      return {
        title: t.inputTitleGeogebra,
        icon: "ri-draft-line",
        defaultPlaceholder: placeholder || t.geogebraPlaceholder,
      };
    } else if (mode === "qq") {
      return {
        title: t.inputTitleQQ || "输入你的需求",
        icon: "ri-mind-map",
        defaultPlaceholder: placeholder || t.qqPlaceholder,
      };
    } else {
      return {
        title: t.inputTitleManim,
        icon: "ri-magic-line",
        defaultPlaceholder: placeholder || t.inputPlaceholder,
      };
    }
  }, [mode, t, placeholder]);

  // --- 打字机轮播效果相关 ---
  const [currentPlaceholder, setCurrentPlaceholder] = useState("");
  const [suggestionIndex, setSuggestionIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCursor, setShowCursor] = useState(true);

  // 获取当前模式的所有示例提示词
  const currentModeSuggestions = useMemo(() => {
    if (t && t.suggestions && t.suggestions[mode]) {
      return t.suggestions[mode];
    }
    return [];
  }, [mode, t]);

  // 当模式或提示词列表改变时，重置打字机状态
  useEffect(() => {
    setSuggestionIndex(0);
    setIsDeleting(false);
    setCurrentPlaceholder("");
  }, [mode, currentModeSuggestions]);

  // 光标闪烁定时器
  useEffect(() => {
    const blinkTimer = setInterval(() => {
      setShowCursor((prev) => !prev);
    }, 500);
    return () => clearInterval(blinkTimer);
  }, []);

  // 打字机状态控制
  useEffect(() => {
    // 如果没有示例提示词，则直接使用原有的默认占位符
    if (!currentModeSuggestions || currentModeSuggestions.length === 0) {
      setCurrentPlaceholder(placeholder || uiTexts.defaultPlaceholder);
      return;
    }

    let timer;
    const currentFullText = currentModeSuggestions[suggestionIndex % currentModeSuggestions.length] || "";

    if (isDeleting) {
      // 正在删除字符
      timer = setTimeout(() => {
        setCurrentPlaceholder((prev) => prev.slice(0, -1));
      }, 40); // 删除速度稍微加快
    } else {
      // 正在输入字符
      timer = setTimeout(() => {
        setCurrentPlaceholder((prev) => currentFullText.slice(0, prev.length + 1));
      }, 100); // 输入速度
    }

    // 逻辑状态切换
    if (!isDeleting && currentPlaceholder === currentFullText) {
      // 当前提示词输入完毕，等待 3.5 秒后开始删除
      timer = setTimeout(() => {
        setIsDeleting(true);
      }, 3500);
    } else if (isDeleting && currentPlaceholder === "") {
      // 删除完毕，切换到下一个提示词
      setIsDeleting(false);
      setSuggestionIndex((prev) => prev + 1);
    }

    return () => clearTimeout(timer);
  }, [currentPlaceholder, isDeleting, suggestionIndex, currentModeSuggestions, placeholder, uiTexts.defaultPlaceholder]);

  // 渲染最终的占位符内容（带闪烁光标效果）
  const displayPlaceholder = useMemo(() => {
    if (!currentModeSuggestions || currentModeSuggestions.length === 0) {
      return placeholder || uiTexts.defaultPlaceholder;
    }
    const currentFullText = currentModeSuggestions[suggestionIndex % currentModeSuggestions.length] || "";
    const isFinishedTyping = !isDeleting && currentPlaceholder === currentFullText;

    if (isFinishedTyping) {
      return currentPlaceholder + (showCursor ? "|" : " ");
    }
    return currentPlaceholder + "|";
  }, [currentPlaceholder, isDeleting, showCursor, currentModeSuggestions, suggestionIndex, placeholder, uiTexts.defaultPlaceholder]);

  const handleInputChange = (e) => {
    const text = e.target.value;
    setInput(text);
    // 自动调整高度
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
  };

  const updatePromptHistory = (updater) => {
    setPromptHistory((previous) => {
      const next = normalizePromptHistory(updater(previous));
      persistPromptHistory(next);
      return next;
    });
  };

  const rememberPrompt = (text) => {
    const normalizedText = text.trim();
    if (!normalizedText) return;

    updatePromptHistory((previous) => {
      const existing = previous.find((item) => item.text === normalizedText);
      return [
        {
          text: normalizedText,
          isFavorite: existing?.isFavorite || false,
          updatedAt: Date.now(),
        },
        ...previous.filter((item) => item.text !== normalizedText),
      ];
    });
  };

  const handleSelectPrompt = (text) => {
    if (isLoading) return;
    setInput(text);

    requestAnimationFrame(() => {
      const textarea = textareaRef.current;
      if (!textarea) return;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 150) + 'px';
      textarea.focus();
      textarea.setSelectionRange(text.length, text.length);
    });
  };

  const handleToggleFavorite = (text) => {
    updatePromptHistory((previous) => previous.map((item) => (
      item.text === text ? { ...item, isFavorite: !item.isFavorite } : item
    )));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const checkResult = checkSensitiveContent(input);
    if (checkResult.hasSensitive) {
      if (checkResult.type === "political") {
        alert("您的输入包含政治敏感内容，请修改后再试。");
      } else if (checkResult.type === "model") {
        alert("您的输入涉及模型原理或底座追问，请修改后再试。");
      } else {
        alert("您的输入包含敏感内容，请修改后再试。");
      }
      return;
    }

    const submittedPrompt = input.trim();
    rememberPrompt(submittedPrompt);
    onSubmit(submittedPrompt, imageFiles);
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((file) => processImageFile(file));
    // 清空 input 值，允许重复选相同文件
    e.target.value = "";
  };

  // 处理图片文件的通用函数（添加到数组，最多 MAX_IMAGES 张）
  const processImageFile = (file) => {
    if (!file.type.startsWith("image/")) {
      alert(t.inputFileTypeError);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t.inputFileSizeError);
      return;
    }

    setImageFiles((prev) => {
      if (prev.length >= MAX_IMAGES) {
        alert(t.inputMaxImagesError || `最多只能上传 ${MAX_IMAGES} 张图片`);
        return prev;
      }
      return [...prev, file];
    });

    const reader = new FileReader();
    reader.onload = (evt) => {
      setImagePreviews((prev) => {
        if (prev.length >= MAX_IMAGES) return prev;
        return [...prev, evt.target.result];
      });
    };
    reader.readAsDataURL(file);
  };

  // 处理粘贴事件
  const handlePaste = (e) => {
    const clipboardData = e.clipboardData || window.clipboardData;
    if (!clipboardData) {
      return;
    }

    // 检查剪贴板中是否包含图片
    const items = clipboardData.items;
    let imageItem = null;

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf("image") !== -1) {
        imageItem = items[i];
        break;
      }
    }

    if (imageItem) {
      const blob = imageItem.getAsFile();
      processImageFile(blob);
      e.preventDefault();
    }
  };

  // 添加粘贴事件监听器
  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.addEventListener("paste", handlePaste);
    }

    return () => {
      if (textarea) {
        textarea.removeEventListener("paste", handlePaste);
      }
    };
  }, []);

  const triggerFileUpload = () => {
    fileInputRef.current.click();
  };

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="input-container">
      {/* 移除原有的 input-header 标题栏 */}
      
      <form onSubmit={handleSubmit} className="input-area-form" style={{ position: 'relative' }}>
        
        {/* 移动端模式切换药丸栏 (模仿 Gemini) */}
        {isMobile && availableModes.length > 0 && (
          <div className="mobile-mode-pills-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', gap: '6px', overflowX: 'auto', flex: 1, paddingRight: '10px' }} className="mobile-mode-pills-scroll">
              {availableModes.map((m) => (
                <button
                  key={m.key}
                  type="button"
                  className={`mobile-mode-pill ${mode === m.key ? 'active' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onModeChange) onModeChange(m.key, input);
                  }}
                >
                  <i className={m.icon}></i>
                  <span>{
                    m.key === 'manim' ? '动画' :
                    m.key === 'manimgx' ? 'ManimGX' :
                    m.key === 'geogebra' ? 'GGb' :
                    m.key === 'threejs' ? '3D' :
                    (m.name || m.key)
                  }</span>
                </button>
              ))}
            </div>
            {/* 3D 视图开关 - 仅在 GeoGebra 模式下于移动端呈现且完美融入模式行 */}
            {mode === "geogebra" && setIs3DMode && (
              <label 
                className="switch-label" 
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  cursor: 'pointer', 
                  userSelect: 'none', 
                  padding: '4px 10px', 
                  background: is3DMode ? 'linear-gradient(135deg, rgba(0, 170, 255, 0.15), rgba(0, 122, 255, 0.25))' : 'rgba(255, 255, 255, 0.05)',
                  border: is3DMode ? '1px solid rgba(0, 170, 255, 0.6)' : '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '14px',
                  color: is3DMode ? '#00aaff' : 'rgba(255, 255, 255, 0.6)',
                  fontSize: '11px',
                  fontWeight: is3DMode ? '600' : 'normal',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'all 0.2s ease',
                  marginLeft: 'auto'
                }}
              >
                <input
                  type="checkbox"
                  checked={is3DMode}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setIs3DMode(checked);
                    try {
                      if (window.ggbApplet) {
                        if (checked) {
                          window.ggbApplet.setPerspective("5"); // 5 = 3D Graphics
                        } else {
                          window.ggbApplet.setPerspective("2"); // 返回 2 = 仅几何画板
                        }
                      }
                    } catch (err) {
                      console.error("切换3D视图失败", err);
                    }
                  }}
                  style={{ marginRight: '4px', cursor: 'pointer', transform: 'scale(0.85)', display: 'none' }} // 隐藏原生复选框以保证界面清爽
                />
                <i className="ri-building-3-line" style={{ fontSize: '13px' }}></i>
              </label>
            )}
          </div>
        )}

        {/* 图片预览区域（多图水平排列） */}
        {imagePreviews.length > 0 && (
          <div className="image-previews-row">
            {imagePreviews.map((preview, index) => (
              <div key={index} className="image-preview-container">
                <img src={preview} alt={t.previewImage || "预览"} className="image-preview" />
                <button
                  type="button"
                  className="remove-image-btn"
                  onClick={() => removeImage(index)}
                  disabled={isLoading}
                  title={t.inputRemoveImage || "移除图片"}
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* 积分消耗胶囊与适用场景固定在输入框左上方 */}
        {!isLoading && (
          <div className="mode-explanation-panel">
            <div className="usage-mode-capsule" role="radiogroup" aria-label="生成积分模式选择">
              {/* 免费模式（仅当该模式配置了免费通道时显示） */}
              {freeModeAvailable && (
                <button
                  type="button"
                  role="radio"
                  aria-checked={isFreeMode}
                  className={`usage-mode-segment ${isFreeMode ? "active free-active" : ""}`}
                  onClick={() => handleUsageTierSelect("free")}
                  title="免费模式：使用基础免费模型生成，不扣除积分"
                >
                  <i className="ri-leaf-line"></i>
                  <span className="segment-label">{t.freeModeTitle || "免费"}</span>
                </button>
              )}

              {/* 标准模式（单次积分扣除） */}
              <button
                type="button"
                role="radio"
                aria-checked={!isFreeMode && !isDeepThinking}
                className={`usage-mode-segment ${!isFreeMode && !isDeepThinking ? "active standard-active" : ""}`}
                onClick={() => handleUsageTierSelect("standard")}
                title={`标准模式：${displayCredits} ${t.creditsUnit || "积分"}/次，直接由AI进行快速生成`}
              >
                <i className="ri-flashlight-fill"></i>
                <span className="segment-label">{displayCredits} {t.creditsUnit || "积分"}</span>
              </button>

              {/* 深度思考模式（胶囊最右端，由原AI修饰合并升级，一步到位自动优化与生成） */}
              {showEnhanceButton && (
                <button
                  type="button"
                  role="radio"
                  aria-checked={isDeepThinking}
                  className={`usage-mode-segment deep-segment ${isDeepThinking ? "active deep-active" : ""}`}
                  onClick={() => handleUsageTierSelect("deep")}
                  title={`深度思考：${deepCredits} ${t.creditsUnit || "积分"}/次（含${enhanceCost}积分深度思考）。生成前先由AI专家进行多步深度推理与专业脚本修饰，等待时间更长，生成质量更佳`}
                >
                  <i className="ri-brain-line"></i>
                  <span className="segment-label">{deepCredits} {t.creditsUnit || "积分"}</span>
                  <span className="deep-think-tag">{t.deepThinking || "深度思考"}</span>
                  <span className="deep-think-wait-hint">{t.deepThinkingHint || "(耗时更长)"}</span>
                </button>
              )}
            </div>
            {displayDescription && (
              <>
                <span className="mode-explanation-divider"></span>
                <span className="mode-explanation-desc">
                  <span className="mode-explanation-desc-title">{t.appScene || "适用场景"}:</span>
                  <span className="mode-explanation-desc-text">{displayDescription}</span>
                </span>
              </>
            )}
          </div>
        )}

        {/* 胶囊输入栏 */}
        <div className="input-capsule-bar">
          
          {/* 模式图标已移除 */}


          {/* 上传图标 - 暗示可以传图，最多3张 */}
          <button
            type="button"
            className={`capsule-icon-btn upload-icon-btn ${imageFiles.length > 0 ? 'has-file' : ''}`}
            onClick={triggerFileUpload}
            disabled={isLoading || imageFiles.length >= MAX_IMAGES}
            title={imageFiles.length >= MAX_IMAGES ? (t.inputMaxImagesError || `最多 ${MAX_IMAGES} 张`) : t.inputUploadImage}
          >
            <i className="ri-image-line upload-icon-desktop"></i>
            <i className="ri-add-line upload-icon-mobile"></i>
            {imageFiles.length > 0 && (
              <span className="upload-count-badge">{imageFiles.length}</span>
            )}
          </button>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageUpload}
            style={{ display: "none" }}
            accept="image/*"
            multiple
          />

          {/* 输入框 */}
          <textarea
            ref={textareaRef}
            className="input-capsule-textarea"
            placeholder={displayPlaceholder}
            value={input}
            onChange={handleInputChange}
            disabled={isLoading}
            rows={1}
          />

          {/* 提交按钮容器 - 包含花瓣菜单 */}
          <div 
            className="submit-btn-wrapper"
            onMouseEnter={() => !isMobile && setShowModeDropdown(true)}
            onMouseLeave={() => !isMobile && setShowModeDropdown(false)}
          >
            {/* 移动端专用：独立模式切换按钮 (已由上方 mobile-mode-pills 替代，非移动端才渲染花瓣菜单) */}
            {!isMobile && (
              <div className={`mode-petal-menu ${(!input.trim() || showModeDropdown) ? 'visible' : ''}`}>
                {availableModes.map((m, index) => {
                  // 计算每个模式卡片的位置
                  const totalModes = availableModes.length;
                  
                  // 桌面端：右侧半圆 (-80 到 80)
                  const startAngle = -80;
                  const endAngle = 80;
                  
                  const angleStep = (endAngle - startAngle) / (totalModes - 1 || 1);
                  const angle = startAngle + index * angleStep;
                  const radian = (angle * Math.PI) / 180;
                  const radius = 80;
                  
                  // 计算偏移量
                  const x = Math.cos(radian) * radius; // 向右是正数
                  const y = Math.sin(radian) * radius; // 向上是负数

                  return (
                    <button
                      key={m.key}
                      type="button"
                      className={`mode-petal-item ${mode === m.key ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onModeChange) onModeChange(m.key, input);
                        setShowModeDropdown(false);
                      }}
                      style={{
                        '--tx': `${x}px`,
                        '--ty': `${y}px`,
                        '--delay': `${index * 0.05}s`
                      }}
                      title={m.name || m.key}
                    >
                      <i className={m.icon}></i>
                      <span className="mode-name">{m.name || m.key}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              className={`capsule-submit-btn ${isDeepThinking ? 'deep-thinking-submit' : ''}`}
              disabled={isLoading || !input.trim()}
              title={
                isLoading
                  ? t.inputGenerating
                  : `${t.currentMode || '当前模式'}: ${uiTexts.title}${isDeepThinking ? ' · 【' + (t.deepThinking || '深度思考') + '】' : ''} - ${t.clickToGenerate || '点击生成'}`
              }
            >
              {isLoading ? (
                 <i className="ri-loader-4-line rotating"></i>
              ) : (
                <div className="current-mode-icon">
                  {/* 移动端显示发送图标，桌面端显示模式图标 */}
                  <i className={`submit-icon-desktop ${availableModes.find(m => m.key === mode)?.icon || "ri-apps-line"}`}></i>
                  <i className="submit-icon-mobile ri-arrow-up-line"></i>
                </div>
              )}
            </button>
             {/* 移除之前的 mobile-mode-trigger div，使用上面的 button 替代 */}
          </div>
        </div>

        {promptHistory.length > 0 && (
          <section className={`prompt-history-panel ${isLoading ? 'is-disabled' : ''}`} aria-label="提示词历史记录">
            <div className="prompt-history-header">
              <span className="prompt-history-title">
                <i className="ri-history-line" aria-hidden="true"></i>
                最近使用
              </span>
              <span className="prompt-history-count">
                {promptHistory.filter((item) => item.isFavorite).length > 0
                  ? `已收藏 ${promptHistory.filter((item) => item.isFavorite).length} 条`
                  : `${promptHistory.length} 条记录`}
              </span>
            </div>
            <div className="prompt-history-list">
              {[...promptHistory]
                .sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite) || b.updatedAt - a.updatedAt)
                .map((item) => (
                  <div className={`prompt-history-item ${item.isFavorite ? 'is-favorite' : ''}`} key={item.text}>
                    <button
                      type="button"
                      className="prompt-history-text"
                      onClick={() => handleSelectPrompt(item.text)}
                      disabled={isLoading}
                      title={item.text}
                    >
                      {item.text}
                    </button>
                    <button
                      type="button"
                      className="prompt-favorite-btn"
                      onClick={() => handleToggleFavorite(item.text)}
                      disabled={isLoading}
                      aria-pressed={item.isFavorite}
                      aria-label={item.isFavorite ? `取消收藏：${item.text}` : `收藏：${item.text}`}
                      title={item.isFavorite ? "取消收藏" : "收藏提示词"}
                    >
                      <i className={item.isFavorite ? "ri-star-fill" : "ri-star-line"} aria-hidden="true"></i>
                    </button>
                  </div>
                ))}
            </div>
          </section>
        )}

        {isLoading && (
          <div className="progress-bar">
            <div className="progress-fill"></div>
          </div>
        )}
      </form>

      {/* 提示词推荐卡片与额外内容 */}
      {!isLoading && extraContent && (
        <div className="input-bottom-area">
          <div className="extra-content-wrapper" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            {extraContent}
          </div>
        </div>
      )}

    </div>
  );
};

export default InputArea;
